import React from "react";
import moment from "moment";
import round from "lodash/round";
import { withTranslation } from "react-i18next";

import NavigationComponent from "../components/NavigationComponent";
import RangeContainerComponent from "../readiness/RangeContainerComponent";
import { RPEList } from "../session-rpe/RPEList";
import { DATE_FORMATS } from "../../../../constants";
import {
  getLabels,
  readyChartData,
  mapUtcToDatePicker,
  filterUnsetValues,
  sortStatsByDate,
  filterStatsbyDateRange,
} from "../../../../utils/daily-stats";
import { AthleteLinks } from "../../../../parts/athlete-links";
import BarChartComponent from "./BarGraph";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { ReportsBackHandler } from "../components/ReportsBackHandler";
import { AthleteSelect } from "../../../../parts/inputs/athlete-select-new";
import SvgIcon from "../../../../parts/SvgIcon";
import { RillContext } from "../../../../rill/RillReact/share/base";

class StepCount extends React.Component {
  constructor(props) {
    super(props);
    this.field = "steps";
    this.precision = 1;
    this.state = {
      currentAthlete: null,
      chartData: [],
      dateFrom: null,
      dateTo: null,
      quickDisabled: false,
      rangeSelector: {
        type: "months",
        value: 6,
      }, // in Weeks format
      statsData: [],
      hoveredDate: "",
      measureSelected: "",
      comesFromBodyComp: props.comesFromBodyComp,
      sortedRecent: true,
      sortedTableData: null,
    };

    this.updateState = this.updateState.bind(this);
    this.updateHoveredStat = this.updateHoveredStat.bind(this);
    this.getDateFirstEntry = this.getDateFirstEntry.bind(this);
    this.sortList = this.sortList.bind(this);
  }

  componentDidMount() {
    const { value, type } = this.state.rangeSelector;
    // sorting most recent to oldest
    const stats = sortStatsByDate(
      filterUnsetValues(this.context.locals.dailyStats, this.field)
    );

    this.setState({
      dateFrom: moment(new Date(), DATE_FORMATS.DATE_DAY_FORMAT)
        .subtract(value, type)
        .format(DATE_FORMATS.DATE_PICKER_FORMAT),
      statsData: stats,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const athleteId = this.context.locals.athlete
      ? this.context.locals.athlete._id
      : this.context.locals.activeUser._id;

    //if the athlete is changed
    if (athleteId !== this.state.currentAthlete) {
      //if the range date is not lifetime, we only update the athlete

      if (this.state.rangeSelector.type !== "lifetime") {
        this.setState({ currentAthlete: athleteId });
      } else {
        //if range date is lifetime we set a default range
        this.setState({
          currentAthlete: athleteId,
          dateFrom: moment("2015-12-31T07:57:45.121Z").format(
            DATE_FORMATS.DATE_PICKER_FORMAT
          ),
        });
      }
    }

    //change the stats state to update the render
    if (
      prevState.currentAthlete !== null &&
      this.state.currentAthlete !== athleteId
    ) {
      const stats = sortStatsByDate(
        filterUnsetValues(this.context.locals.dailyStats, this.field)
      );

      this.setState({
        statsData: stats,
      });
    }
  }

  updateHoveredStat(date = "") {
    this.setState({ hoveredDate: date });
  }

  calculateAvg(stats, field, precision) {
    const total = stats
      .map((stat) => stat[field])
      .reduce((prev, current) => prev + current, 0);

    const avg = round(total / stats.length, precision);
    return avg;
  }

  getDateFirstEntry() {
    const firstEntry = this.context.locals.dailyStats
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .find((stats) => stats.steps);
    return firstEntry && firstEntry.date;
  }

  updateState(state) {
    this.setState(state);
  }
  sortList(newState) {
    this.setState({
      ...this.state,
      sortedTableData: newState.dateFormattedData,
      sortedRecent: newState.sortedRecent,
    });
  }
  formatNumber(number) {
    const x = Number(number);
    const formatter = new Intl.NumberFormat(undefined, {
      notation: x >= 1e3 ? "compact" : "standard",
    });
    return formatter.format(x);
  }

  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const { query, pathname, athlete } = locals;
    const { dateTo, dateFrom, hoveredDate, statsData, comesFromBodyComp } =
      this.state;

    // sorted most recent to oldest
    const listData = filterStatsbyDateRange({
      stats: statsData,
      dateTo,
      dateFrom,
    });

    const avg = this.calculateAvg(listData, this.field, 0);
    const headerRight = {
      name: "Average ",
      value: avg ? `${Number(avg).toLocaleString()} steps` : 0,
    };

    let [chartData, labels] = [
      readyChartData({ stats: listData, field: this.field }),
      getLabels(listData).reverse(),
    ];

    const colors = ["rgba(91, 197, 0, 1)"];

    const statsList = mapUtcToDatePicker({
      stats: listData,
      fieldToConvert: "utcDate",
      newField: "date",
    });

    const columnsData = [
      {
        name: t("reports.date"),
        field: "date",
        columnClass: "date",
        itemClass: "date",
      },
      {
        name: t("stepCount.steps"),
        field: this.field,
        columnClass: "steps",
        itemClass: "steps",
      },
    ];

    const chartOptions = {
      showLine: true,
      ticks: [],
      axisY: {
        offset: 50,
        labelOffset: {
          x: 5,
          y: 5,
        },
        labelInterpolationFnc: (value) => this.formatNumber(value),
      },
    };

    return (
      <main role="reports-wrapper">
        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athlete}
            pathName={pathname}
            classNames={"only-show-mobile"}
            query={query}
          />
          <div className="athlete-select dropdown-athlete">
            <div
              className="avatar"
              style={{
                backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
              }}
            />
            <AthleteSelect locals={locals} />
          </div>
        </div>
        <NavigationComponent
          query={query}
          textKey="nav.calendarDayView"
          pathName={pathname}
          classNames="mobile-hidden"
        />
        <article id="step-count" role="report-content">
          <ReportsBackHandler title={t("stepCount.title")} locals={locals} />
          <header className="mobile-hidden">
            <hgroup>
              <div className="flex gap-2 items-center">
                {comesFromBodyComp ? (
                  <SvgIcon
                    classNames="cursor-pointer "
                    onClickFn={() => {
                      history.back();
                    }}
                    src={"/img/new-icons/back-button.svg"}
                  />
                ) : (
                  <></>
                )}
                <h2 className="mobile-hidden">{t("stepCount.title")}</h2>
              </div>
            </hgroup>
          </header>
          <RangeContainerComponent
            {...this.state}
            key={1}
            updateState={(state) => {
              this.updateState({
                ...state,
                sortedTableData: null,
              });
            }}
            getDateFirstEntry={this.getDateFirstEntry}
            locals={locals}
          />
          <BarChartComponent
            bodyMeasurement={true}
            dateTo={dateTo}
            dateFrom={dateFrom}
            series={chartData}
            labels={labels}
            chartName={"Steps"}
            displayLeyend={false}
            colors={colors}
            areaOpacity={0.3}
            updateHoveredStat={this.updateHoveredStat}
            chartOptions={chartOptions}
            headerRight={headerRight}
            tooltipUnit={" steps"}
            formatTooltip
            type="Bar"
          />
          <div className="mobile-hidden">
            <RPEList
              dateFrom={this.state.dateFrom}
              dateTo={this.state.dateTo}
              list={this.state.sortedTableData || statsList}
              columnsData={columnsData}
              highlightedElem={hoveredDate}
              sortedRecent={this.state.sortedRecent}
              updateState={this.sortList}
            />
          </div>

          <div className="only-show-mobile">
            <RPEList
              dateFrom={this.state.dateFrom}
              dateTo={this.state.dateTo}
              list={this.state.sortedTableData || statsList}
              columnsData={columnsData}
              highlightedElem={hoveredDate}
              sortedRecent={this.state.sortedRecent}
              updateState={this.sortList}
            />
          </div>
        </article>
      </main>
    );
  }
}

StepCount.contextType = RillContext;

export default withTranslation("athlete-portal")(StepCount);
